import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { formatDate, formatNumber } from '@/lib/format'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  RowMenu,
  SearchBox,
  SkeletonTable,
  Switch,
  isForbidden,
} from '@/admin/AdminShell'
import type { PriceList, PriceListInput } from './pricingApi'
import { PRICE_LISTS_KEY, RESOURCE, createPriceList, deletePriceList, fetchPriceLists, updatePriceList } from './pricingApi'

/**
 * Прайс-листы (раздел «Продажи → Прайс-листы»).
 *
 * Список прайсов со сроком, признаком «по умолчанию», числом цен и клиентов, которым прайс
 * назначен; цены — на странице прайса (`/price-lists/:id`).
 *
 * Правила держит сервер, экран их называет:
 * - **одно умолчание на валюту и день** — второй прайс по умолчанию с пересекающимся сроком
 *   отвергается (`pricelist.default_overlap`), отказ показывается словами сервера;
 * - **валюта прайса с ценами не меняется** — поле валюты у такого прайса только для чтения:
 *   цены переименовались бы, а не пересчитались;
 * - **прайс, назначенный клиентам, не удаляется** — пункт меню виден с причиной.
 *
 * Без права правки — баннер «только просмотр» и ни одной кнопки правки.
 */
export function PriceListsPage() {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const lists = useQuery({ queryKey: PRICE_LISTS_KEY, queryFn: ({ signal }) => fetchPriceLists(signal) })
  const [draft, setDraft] = useState<PriceListDraft | null>(null)
  const [removing, setRemoving] = useState<PriceList | null>(null)
  const [query, setQuery] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const mayCreate = can(user, RESOURCE, 'create')
  const mayEdit = can(user, RESOURCE, 'update')
  const mayDelete = can(user, RESOURCE, 'delete')
  const forbidden = isForbidden(lists.error)

  const all = lists.data ?? []
  const needle = query.trim().toLowerCase()
  const shown = all.filter((list) => !needle || [list.code, list.name, list.currency].some((v) => v.toLowerCase().includes(needle)))

  const remove = useMutation({
    mutationFn: (id: string) => deletePriceList(id),
    onSuccess: () => {
      setRemoving(null)
      void queryClient.invalidateQueries({ queryKey: PRICE_LISTS_KEY })
    },
    onError: (error) => {
      setRemoving(null)
      setNotice(error instanceof Error ? error.message : t('app.error'))
    },
  })

  return (
    <AdminPage
      sectionKey="nav.section.sales"
      titleKey="nav.priceLists"
      count={lists.data ? all.length.toLocaleString(locale) : undefined}
      leadKey="pl.lead"
      readOnly={!mayEdit && !mayCreate}
      roKey="pl.ro"
      forbidden={forbidden}
      primary={mayCreate && <PrimaryButton label={t('pl.add')} onClick={() => setDraft(newDraft())} />}
    >
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('pl.search')} />
        </div>
      )}
      <Notice text={notice} />
      <AdminState
        isLoading={lists.isLoading}
        error={lists.error}
        onRetry={() => void lists.refetch()}
        forbiddenTitleKey="pl.forbidden"
        errorTitleKey="pl.error"
        skeleton={<SkeletonTable columns={8} rows={5} />}
      >
        {all.length === 0 ? (
          <StateView boxed kind="empty" title={t('pl.empty')} description={t('pl.empty.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('pl.notFound', { query })}
                     actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>} />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('nav.priceLists')}>
              <thead>
                <tr>
                  <th>{t('pl.code')}</th>
                  <th>{t('pl.name')}</th>
                  <th>{t('pl.currency')}</th>
                  <th>{t('pl.period')}</th>
                  <th className="num">{t('pl.priority')}</th>
                  <th>{t('pl.state')}</th>
                  <th className="num">{t('pl.items')}</th>
                  <th className="num">{t('pl.counterparties')}</th>
                  {(mayEdit || mayDelete) && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((list) => {
                  const items: MenuAction[] = []
                  if (mayEdit) items.push({ id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setDraft(editDraft(list)) })
                  if (mayDelete) {
                    items.push({
                      id: 'delete',
                      label: t('pl.delete'),
                      icon: 'trash',
                      danger: true,
                      onSelect: () => setRemoving(list),
                      disabledReason: list.counterparties > 0 ? t('pl.deleteBlocked', { count: list.counterparties }) : undefined,
                    })
                  }
                  return (
                    <tr key={list.id} className={draft?.id === list.id ? 'is-selected' : undefined}>
                      <td><Link to={`/price-lists/${list.id}`} className="mono strong">{list.code}</Link></td>
                      <td>
                        <Link to={`/price-lists/${list.id}`}>{list.name}</Link>
                        {list.defaultList && <span className="badge b-info" style={{ marginLeft: 6 }}>{t('pl.default')}</span>}
                      </td>
                      <td className="mono">{list.currency}</td>
                      <td>{period(list, t, locale)}</td>
                      <td className="num">{formatNumber(list.priority, locale)}</td>
                      <td><StateBadge list={list} /></td>
                      <td className="num">{formatNumber(list.items, locale)}</td>
                      <td className="num">{formatNumber(list.counterparties, locale)}</td>
                      {(mayEdit || mayDelete) && (
                        <td className="adm-act">
                          <RowMenu label={t('pl.actions', { code: list.code })} items={items} />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminState>
      {draft && <PriceListSheet draft={draft} onChange={setDraft} onClose={() => setDraft(null)} />}
      <ConfirmModal
        open={removing != null}
        title={t('pl.deleteConfirm', { name: removing?.name ?? '' })}
        confirmLabel={t('pl.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p>{t('pl.deleteConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

export function period(list: Pick<PriceList, 'validFrom' | 'validUntil'>, t: (key: string) => string, locale: Parameters<typeof formatDate>[1]): string {
  const from = list.validFrom ? formatDate(list.validFrom, locale) : t('pl.period.open')
  const until = list.validUntil ? formatDate(list.validUntil, locale) : t('pl.period.open')
  if (!list.validFrom && !list.validUntil) return t('pl.period.always')
  return `${from} — ${until}`
}

export function StateBadge({ list }: { list: PriceList }) {
  const { t } = useApp()
  if (!list.active) return <span className="badge"><span className="dot" aria-hidden="true" />{t('pl.state.inactive')}</span>
  if (!list.effectiveToday) return <span className="badge b-warning"><span className="dot" aria-hidden="true" />{t('pl.state.outOfPeriod')}</span>
  return <span className="badge b-success"><span className="dot" aria-hidden="true" />{t('pl.state.active')}</span>
}

// ------------------------------------------------------------------ правка

export interface PriceListDraft {
  /** null — новый прайс. */
  id: string | null
  version?: number
  code: string
  name: string
  currency: string
  priority: string
  validFrom: string
  validUntil: string
  active: boolean
  defaultList: boolean
  /** У прайса уже есть цены — валюта не меняется. */
  hasItems: boolean
}

export function newDraft(): PriceListDraft {
  return { id: null, code: '', name: '', currency: 'RUB', priority: '0', validFrom: '', validUntil: '', active: true, defaultList: false, hasItems: false }
}

export function editDraft(list: PriceList): PriceListDraft {
  return {
    id: list.id,
    version: list.version,
    code: list.code,
    name: list.name,
    currency: list.currency,
    priority: String(list.priority),
    validFrom: list.validFrom ?? '',
    validUntil: list.validUntil ?? '',
    active: list.active,
    defaultList: list.defaultList,
    hasItems: list.items > 0,
  }
}

export function PriceListSheet({ draft, onChange, onClose, onSaved }: {
  draft: PriceListDraft
  onChange: (draft: PriceListDraft) => void
  onClose: () => void
  onSaved?: (list: PriceList) => void
}) {
  const { t } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const priority = Number(draft.priority)
  const periodError = draft.validFrom !== '' && draft.validUntil !== '' && draft.validUntil < draft.validFrom ? t('pl.periodInvalid') : null
  const currencyError = !/^[A-Za-z]{3}$/.test(draft.currency.trim()) ? t('pl.currencyInvalid') : null
  const incomplete = draft.name.trim() === '' || currencyError != null || periodError != null || !Number.isInteger(priority)

  const save = useMutation({
    mutationFn: () => {
      const body: PriceListInput = {
        code: draft.id ? undefined : draft.code.trim() || undefined,
        name: draft.name.trim(),
        currency: draft.currency.trim().toUpperCase(),
        priority,
        validFrom: draft.validFrom || null,
        validUntil: draft.validUntil || null,
        active: draft.active,
        defaultList: draft.defaultList,
        version: draft.version,
      }
      return draft.id ? updatePriceList(draft.id, body) : createPriceList(body)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: PRICE_LISTS_KEY })
      onSaved?.(saved)
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('pl.form')}
      title={draft.id ? `${draft.code} — ${draft.name}` : t('pl.new')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={draft.id ? t('admin.save') : t('pl.add')}
                            pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('pl.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          <Field label={t('pl.name')} htmlFor="pl-name" required>
            <input id="pl-name" className="input" maxLength={255} value={draft.name}
                   onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
          {draft.id ? (
            <Field label={t('pl.code')} help={t('pl.codeLocked')}>
              <ReadonlyValue mono>{draft.code}</ReadonlyValue>
            </Field>
          ) : (
            <Field label={t('pl.code')} htmlFor="pl-code" help={t('pl.code.help')}>
              <input id="pl-code" className="input mono" maxLength={64} value={draft.code}
                     onChange={(e) => onChange({ ...draft, code: e.target.value })} />
            </Field>
          )}
        </div>
        <div className="form-grid">
          <Field label={t('pl.currency')} htmlFor="pl-currency" required
                 error={draft.hasItems ? null : currencyError}
                 help={draft.hasItems ? t('pl.currencyLocked') : t('pl.currency.help')}>
            {draft.hasItems ? (
              <ReadonlyValue mono>{draft.currency}</ReadonlyValue>
            ) : (
              <input id="pl-currency" className="input mono" maxLength={3} value={draft.currency}
                     onChange={(e) => onChange({ ...draft, currency: e.target.value.toUpperCase() })} />
            )}
          </Field>
          <Field label={t('pl.priority')} htmlFor="pl-priority" help={t('pl.priority.help')}>
            <input id="pl-priority" className="input mono" inputMode="numeric" value={draft.priority}
                   onChange={(e) => onChange({ ...draft, priority: e.target.value })} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('pl.validFrom')} htmlFor="pl-from" help={t('pl.validFrom.help')}>
            <input id="pl-from" type="date" className="input" value={draft.validFrom}
                   onChange={(e) => onChange({ ...draft, validFrom: e.target.value })} />
          </Field>
          <Field label={t('pl.validUntil')} htmlFor="pl-until" error={periodError} help={t('pl.validUntil.help')}>
            <input id="pl-until" type="date" className="input" value={draft.validUntil}
                   onChange={(e) => onChange({ ...draft, validUntil: e.target.value })} />
          </Field>
        </div>
        <Field label={t('pl.default')} help={t('pl.default.help')}>
          <Switch checked={draft.defaultList} ariaLabel={t('pl.default')}
                  label={draft.defaultList ? t('pl.default.on') : t('pl.default.off')}
                  onChange={(value) => onChange({ ...draft, defaultList: value })} />
        </Field>
        <Field label={t('pl.active')} help={t('pl.active.help')}>
          <Switch checked={draft.active} ariaLabel={t('pl.active')} onChange={(value) => onChange({ ...draft, active: value })} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
